import { or } from 'tio-ui/utilities';
import { Divider } from 'tio-ui/components/utilities';
import { eq } from 'tio-ui/utilities';
import { Footer, Modal } from 'tio-ui/components/modal';
import { hash } from '@ember/helper';
import { htmlSafe } from '@ember/template';
import { on } from '@ember/modifier';
import { PhCurrencyCircleDollar, PhX } from 'ember-phosphor-icons';
import { RadioGroup } from 'tio-ui/components/forms';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import CourseCashTag from 'tio-common/components/tas/course-cash-tag';
import DescriptionList from 'tio-ui/components/description-list';
import { divide } from 'tio-ui/math';
import ReimbursementPopoverTag from 'tio-common/components/tas/reimbursement-popover-tag';
import safeFormatNumber from 'tio-common/helpers/safe-format-number';
import screenIs from 'tio-common/helpers/screen-is';
import Table from 'tio-ui/components/table';
import TasApplicationModel from 'tio-common/models/tas-application';
import type Owner from '@ember/owner';
import TasCardHolder from 'tio-employee/components/tas/course-cash/card-holder';

interface PaymentMethodAndSubtotalsModalSignature {
  Args: {
    show: boolean;
    cancel: () => void;
    application: TasApplicationModel;
    paymentMethod: string;
    setPaymentMethod: (method: string) => void;
  };
}

export default class PaymentMethodAndSubtotalsModalComponent extends Component<PaymentMethodAndSubtotalsModalSignature> {
  @tracked showReimbursementBanner = false;

  constructor(owner: Owner, args: PaymentMethodAndSubtotalsModalSignature['Args']) {
    super(owner, args);
    this.showReimbursementBanner = args.paymentMethod === 'TAS.AssistanceManner.REIMBURSEMENT';
  }

  get showCardHolder() {
    return (
      this.args.paymentMethod === 'TAS.AssistanceManner.COURSE_CASH' &&
      this.args.application.tasProgramInstance.employee.pcsCardHolders.length === 0
    );
  }

  updatePaymentMethod = (selected: string) => {
    if (selected === 'TAS.AssistanceManner.REIMBURSEMENT') {
      this.showReimbursementBanner = true;
    } else {
      this.showReimbursementBanner = false;
    }

    this.args.setPaymentMethod(selected);
  };

  closeReimbursementBanner = () => {
    this.showReimbursementBanner = false;
  };

  <template>
    <Modal
      @isOpen={{@show}}
      @onClose={{@cancel}}
      @classes={{hash base="max-w-3xl"}}
      @size="xl"
      @closeOnOutsideClick={{false}}
      as |m|
    >
      <m.Header>{{t "tas.payment_method.header"}}</m.Header>
      <m.Body>
        <Table @isLoading={{false}}>
          <:thead as |options|>
            <options.tr>
              <options.th>{{t "tas.payment_method.expense_type"}}</options.th>
              <options.th class="text-center">{{t "tas.payment_method.amount"}}</options.th>
              <options.th class="pl-12">{{t "tas.payment_method.default"}}</options.th>
            </options.tr>
          </:thead>
          <:tbody as |options|>
            {{! Tuition }}
            <options.tr>
              <options.td
                class={{if (or this.showCardHolder this.showReimbursementBanner) "border-0!"}}
              >
                {{t "tas.payment_method.tuition"}}
              </options.td>
              <options.td
                class="text-center
                  {{if (or this.showCardHolder this.showReimbursementBanner) 'border-0!'}}"
              >
                {{safeFormatNumber
                  (divide @application.requestedCourseCashSubtotal 100)
                  style="currency"
                  currency="USD"
                }}
              </options.td>
              <options.td
                class={{if (or this.showCardHolder this.showReimbursementBanner) "border-0!"}}
              >
                <RadioGroup
                  @name="tuitionPaymentMethod"
                  @value={{@paymentMethod}}
                  @onChange={{this.updatePaymentMethod}}
                  @orientation={{if (screenIs "sm") "horizontal" "vertical"}}
                  @classes={{hash base="pl-8" label="hidden"}}
                  @isRequired={{true}}
                  as |Radio|
                >
                  <label for="course-cash-radio" class="flex items-center gap-x-2">
                    <Radio
                      @value="TAS.AssistanceManner.COURSE_CASH"
                      data-test-course-cash-radio
                      id="course-cash-radio"
                      aria-label={{t "tas.payment_method.course_cash_payment_method"}}
                    />
                    <CourseCashTag
                      @isDirectPayProgram={{true}}
                      @size="sm"
                      class="text-inherit"
                      @buttonClass="text-tio-gray-700"
                      aria-hidden="true"
                    />
                  </label>

                  {{! Reimbursement option }}
                  <label for="reimbursement-radio" class="flex items-center md:pl-6 gap-x-1">
                    <Radio
                      @value="TAS.AssistanceManner.REIMBURSEMENT"
                      data-test-reimbursement-radio
                      id="reimbursement-radio"
                      aria-label={{t "tas.payment_method.reimbursement_payment_method"}}
                    />
                    <ReimbursementPopoverTag @size="sm" @disablePopover={{true}} />
                  </label>
                </RadioGroup>
              </options.td>
            </options.tr>

            {{#if this.showCardHolder}}
              <options.tr class="p-0!">
                <options.td colspan="3" class="pt-0!">
                  <CardHolderBanner @application={{@application}} />
                </options.td>
              </options.tr>
            {{/if}}

            {{#if this.showReimbursementBanner}}
              <options.tr class="p-0!">
                <options.td colspan="3" class="pt-0!">
                  <ReimbursementBanner @onClose={{this.closeReimbursementBanner}} />
                </options.td>
              </options.tr>
            {{/if}}

            {{! Course Books }}
            <options.tr>
              <options.td>{{t "tas.payment_method.course_books"}}</options.td>
              <options.td class="text-center">
                {{safeFormatNumber
                  (divide @application.requestedBooksSubtotal 100)
                  style="currency"
                  currency="USD"
                }}
              </options.td>
              <options.td>
                <ReimbursementPopoverTag @size="sm" @disablePopover={{true}} class="pl-16" />
              </options.td>
            </options.tr>

            {{! Course Labs }}
            <options.tr>
              <options.td>{{t "tas.payment_method.course_labs"}}</options.td>
              <options.td class="text-center">
                {{safeFormatNumber
                  (divide @application.requestedLabsSubtotal 100)
                  style="currency"
                  currency="USD"
                }}
              </options.td>
              <options.td>
                <ReimbursementPopoverTag @size="sm" @disablePopover={{true}} class="pl-16" />
              </options.td>
            </options.tr>

            {{! Course Software }}
            <options.tr>
              <options.td>{{t "tas.payment_method.course_software"}}</options.td>
              <options.td class="text-center">
                {{safeFormatNumber
                  (divide @application.requestedSoftwareSubtotal 100)
                  style="currency"
                  currency="USD"
                }}
              </options.td>
              <options.td>
                <ReimbursementPopoverTag @size="sm" @disablePopover={{true}} class="pl-16" />
              </options.td>
            </options.tr>

            {{! Course Fees }}
            <options.tr>
              <options.td>{{t "tas.payment_method.course_fees"}}</options.td>
              <options.td class="text-center">
                {{safeFormatNumber
                  (divide @application.requestedFeesSubtotal 100)
                  style="currency"
                  currency="USD"
                }}
              </options.td>
              <options.td>
                <ReimbursementPopoverTag @size="sm" @disablePopover={{true}} class="pl-16" />
              </options.td>
            </options.tr>

          </:tbody>
        </Table>

        <div class="py-6">
          <p class="text-sm font-semibold">{{t "tas.payment_method.payment_method_totals"}}</p>
          <DescriptionList @compact={{true}} as |dl|>
            <dl.Group>
              <dl.Term>
                <CourseCashTag
                  @isDirectPayProgram={{true}}
                  @size="sm"
                  class="text-inherit"
                  @buttonClass="text-tio-gray-700"
                />
              </dl.Term>
              {{#if (eq @paymentMethod "TAS.AssistanceManner.COURSE_CASH")}}
                <dl.Details class="pl-1">
                  {{safeFormatNumber
                    (divide @application.requestedCourseCashSubtotal 100)
                    style="currency"
                    currency="USD"
                  }}
                </dl.Details>
              {{else}}
                <dl.Details class="pl-1">
                  {{safeFormatNumber 0 style="currency" currency="USD"}}
                </dl.Details>
              {{/if}}

            </dl.Group>
            {{#if (eq @paymentMethod "TAS.AssistanceManner.COURSE_CASH")}}
              <dl.Group>
                <dl.Term class="text-xs font-normal md:indent-8">
                  {{t "tas.payment_method.scholarships"}}
                </dl.Term>
                <dl.Details>
                  -{{safeFormatNumber
                    (divide @application.scholarshipsTotal 100)
                    style="currency"
                    currency="USD"
                  }}
                </dl.Details>
              </dl.Group>
              <div class="max-w-xs flex md:justify-end">
                <Divider class="my-2 w-46.25" />
              </div>
              <dl.Group>
                <dl.Term class="text-xs font-normal md:text-right">
                  {{htmlSafe
                    (t
                      "common.tas.course_cash.total"
                      visuallyStyledCourseCash=(t "common.tas.course_cash.default")
                      plainCourseCash=(t "common.tas.course_cash.label")
                    )
                  }}
                </dl.Term>
                <dl.Details class="pl-1">
                  {{safeFormatNumber
                    (divide @application.requestedCourseCashSubtotalLessScholarships 100)
                    style="currency"
                    currency="USD"
                  }}
                </dl.Details>
              </dl.Group>
            {{/if}}
            <div class="max-w-xs">
              <Divider class="my-2" />
            </div>
            <dl.Group>
              <dl.Term><ReimbursementPopoverTag @size="sm" /></dl.Term>
              {{#if (eq @paymentMethod "TAS.AssistanceManner.REIMBURSEMENT")}}
                <dl.Details class="pl-1">
                  {{safeFormatNumber
                    (divide @application.requestedTotalAllCourses 100)
                    style="currency"
                    currency="USD"
                  }}
                </dl.Details>
              {{else}}
                <dl.Details class="pl-1">
                  {{safeFormatNumber
                    (divide @application.requestedNonCourseCashSubtotal 100)
                    style="currency"
                    currency="USD"
                  }}
                </dl.Details>
              {{/if}}
            </dl.Group>

            {{#if (eq @paymentMethod "TAS.AssistanceManner.REIMBURSEMENT")}}
              <dl.Group>
                <dl.Term class="text-xs font-normal md:indent-8">
                  {{t "tas.payment_method.scholarships"}}</dl.Term>
                <dl.Details>
                  -{{safeFormatNumber
                    (divide @application.scholarshipsTotal 100)
                    style="currency"
                    currency="USD"
                  }}
                </dl.Details>
              </dl.Group>
            {{/if}}

            <div class="max-w-xs">
              <Divider class="my-2" />
            </div>
            <dl.Group>
              <dl.Term class="text-xs md:text-right font-normal">
                {{t "tas.payment_method.payment_total"}}
              </dl.Term>
              <dl.Details class="font-semibold pl-1">
                {{safeFormatNumber
                  (divide @application.requestedTotal 100)
                  style="currency"
                  currency="USD"
                }}
              </dl.Details>
            </dl.Group>
          </DescriptionList>
        </div>
      </m.Body>
      <m.Footer>
        <Footer @onCancel={{@cancel}} @onSubmit={{@cancel}} @submitText={{t "common.save"}} />
      </m.Footer>
    </Modal>
  </template>
}

interface CardHolderBannerSignature {
  Args: {
    application: TasApplicationModel;
  };
}

class CardHolderBanner extends Component<CardHolderBannerSignature> {
  @tracked arrowLeft = 0;

  constructor(owner: Owner, args: CardHolderBannerSignature['Args']) {
    super(owner, args);
    // Schedule measurement after DOM is ready
    requestAnimationFrame(() => {
      this.measureArrow();
    });
  }

  measureArrow() {
    const radio = document.getElementById('course-cash-radio');
    const banner = document.getElementById('card-holder-banner');

    if (!radio || !banner) return;

    const radioRect = radio.getBoundingClientRect();
    const bannerRect = banner.getBoundingClientRect();
    const arrowWidth = 16; // w-4 = 1rem = 16px

    this.arrowLeft = radioRect.left + radioRect.width / 2 - bannerRect.left - arrowWidth / 2;
  }

  get arrowPosition() {
    return htmlSafe(`left: ${this.arrowLeft}px;`);
  }

  <template>
    <div id="card-holder-banner" class="relative">
      <div class="absolute w-4 h-4 bg-gray-100 rotate-45 -top-2" style={{this.arrowPosition}}></div>
      <TasCardHolder @application={{@application}} />
    </div>
  </template>
}

interface ReimbursementBannerSignature {
  Args: {
    onClose: () => void;
  };
}

class ReimbursementBanner extends Component<ReimbursementBannerSignature> {
  @tracked arrowLeft = 0;

  constructor(owner: Owner, args: ReimbursementBannerSignature['Args']) {
    super(owner, args);
    // Schedule measurement after DOM is ready
    requestAnimationFrame(() => {
      this.measureArrow();
    });
  }

  measureArrow() {
    const radio = document.getElementById('reimbursement-radio');
    const banner = document.getElementById('reimbursement-banner');

    if (!radio || !banner) return;

    const radioRect = radio.getBoundingClientRect();
    const bannerRect = banner.getBoundingClientRect();
    const arrowWidth = 16; // w-4 = 1rem = 16px

    this.arrowLeft = radioRect.left + radioRect.width / 2 - bannerRect.left - arrowWidth / 2;
  }

  get arrowPosition() {
    return htmlSafe(`left: ${this.arrowLeft}px;`);
  }

  closeBanner = () => {
    this.args.onClose();
  };

  <template>
    <div id="reimbursement-banner" class="relative">
      <div class="absolute w-4 h-4 bg-gray-100 rotate-45 -top-2" style={{this.arrowPosition}}></div>
      <div class="bg-gray-100 p-4 rounded-lg flex items-center justify-between gap-x-4">
        <div class="flex items-center gap-x-4">
          <PhCurrencyCircleDollar @size="24" class="text-tio-gray-700 flex-none" />
          <div>
            <p class="font-semibold">
              {{t "tas.payment_method.reimbursement_selected"}}
            </p>
            <p class="text-sm leading-6">
              {{t "tas.payment_method.reimbursement_description"}}
            </p>
          </div>
        </div>

        <button
          type="button"
          {{on "click" this.closeBanner}}
          aria-label={{t "common.close"}}
          class="text-tio-gray-500 hover:text-tio-gray-700 self-start"
        >
          <PhX @size="16" class="flex-none" />
        </button>
      </div>
    </div>
  </template>
}
